import * as React from "react";
import { useState } from "react";
import {
  Avatar,
  Box,
  Typography,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { UserSettingsDialog } from "./UserSettingsDialog";

export interface AccountSidebarPreviewProps {
  mini?: boolean;
  open?: boolean;
}

export function AccountSidebarPreview(props: AccountSidebarPreviewProps) {
  const { mini = false, open = true } = props;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate("/login");
  };

  const handleSettings = () => {
    handleClose();
    setSettingsOpen(true);
  };

  const getInitials = () => {
    if (user?.firstname && user?.lastname) {
      return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  const getDisplayName = () => {
    if (user?.firstname && user?.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    return user?.email || "User";
  };

  return (
    <Stack direction="column" p={0} onClick={(e) => e.stopPropagation()}>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: mini ? "center" : "flex-start",
          borderRadius: 2,
          cursor: "pointer",
          p: mini ? 0.5 : 1,
          "&:hover": !mini
            ? {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }
            : undefined,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: "#FFFFFF",
            color: "#00A19C",
            fontSize: "0.875rem",
            fontWeight: 600,
            mr: mini ? 0 : 1.5,
            mb: mini ? 0.5 : 0,
          }}
        >
          {getInitials()}
        </Avatar>
        {!mini && open && (
          <Typography
            variant="body2"
            sx={{
              color: "#FFFFFF",
              fontWeight: 500,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getDisplayName()}
          </Typography>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
      <UserSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </Stack>
  );
}
